import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveAssetUrl } from '../lib/assets';
import { formatApiError } from '../lib/errors';
import { clearClsCart } from '../lib/classifiedsCart';
import { ArrowLeft, Loader2, ShoppingBag, Store, CheckCircle2, XCircle, ShieldCheck, Clock, ImageOff, Star, Truck, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARRIERS = ['Omniva', 'DPD', 'Latvijas Pasts', 'Venipak', 'Cits'];

// Official carrier tracking portals (no reliable deep-link param → open the
// page and copy the number to the clipboard so the user can paste it).
const CARRIER_PORTALS = {
  'Omniva': 'https://www.omniva.lv/private/track_and_trace',
  'DPD': 'https://www.dpd.com/lv/en/receiving-parcels/shipment-tracking/',
  'Latvijas Pasts': 'https://pasts.lv/izsekot-sutijumu',
};
const trackingHref = (carrier, nr) => {
  const n = encodeURIComponent((nr || '').trim());
  if (carrier === 'Venipak') return `https://venipak.com/tracking/track/${n}`;
  if (CARRIER_PORTALS[carrier]) return CARRIER_PORTALS[carrier];
  // Unknown / "Cits" carrier → universal auto-detect aggregator (deep link).
  return `https://parcelsapp.com/en/tracking/${n}`;
};

const STATUS_META = {
  paid: { lv: 'Apmaksāts · nauda tiek turēta', en: 'Paid · held in escrow', ru: 'Оплачено', cls: 'bg-amber-100 text-amber-800' },
  completed: { lv: 'Pabeigts', en: 'Completed', ru: 'Завершено', cls: 'bg-green-100 text-green-800' },
  cancelled: { lv: 'Atcelts', en: 'Cancelled', ru: 'Отменено', cls: 'bg-red-100 text-red-700' },
};

export default function ClassifiedDealsPage() {
  const { tr } = useLanguage();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState('buying');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [shipOrder, setShipOrder] = useState(null);
  const [shipTracking, setShipTracking] = useState('');
  const [shipCarrier, setShipCarrier] = useState('');
  const [shipping, setShipping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/classified-orders`, { params: { role: tab } }); setOrders(data || []); }
    catch { setOrders([]); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Stripe return → poll the order status, then refresh.
  useEffect(() => {
    const sid = sp.get('session_id');
    if (sp.get('cls_paid') === '1' && sid) {
      setPolling(true);
      let tries = 0;
      const tick = async () => {
        tries += 1;
        try {
          const { data } = await axios.get(`${API}/classified-orders/status/${sid}`);
          if (data.payment_status === 'paid') {
            clearClsCart();
            toast.success(tr('Apmaksāts! Nauda tiek turēta līdz apstiprini saņemšanu.', 'Paid! Funds held until you confirm receipt.', 'Оплачено!'));
            setPolling(false);
            const next = new URLSearchParams(sp); next.delete('cls_paid'); next.delete('session_id'); setSp(next, { replace: true });
            load();
            return;
          }
        } catch { /* keep trying */ }
        if (tries < 8) setTimeout(tick, 1500); else { setPolling(false); load(); }
      };
      tick();
    } else if (sp.get('cls_cancelled') === '1') {
      toast(tr('Apmaksa atcelta', 'Payment cancelled', 'Оплата отменена'));
      const next = new URLSearchParams(sp); next.delete('cls_cancelled'); setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (oid, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(oid);
    try {
      await axios.post(`${API}/classified-orders/${oid}/${action}`);
      toast.success(
        action === 'complete'
          ? tr('Darījums pabeigts! Pārdevējam pārskaitīta nauda.', 'Deal completed! Seller paid.', 'Сделка завершена!')
          : action === 'mark-shipped'
          ? tr('Atzīmēts kā nosūtīts/nodots. Pircējs informēts.', 'Marked as shipped/handed over. Buyer notified.', 'Отмечено как отправлено. Покупатель уведомлён.')
          : tr('Darījums atcelts', 'Deal cancelled', 'Сделка отменена'));
      await load();
    } catch (e) { toast.error(formatApiError(e, tr('Kļūda', 'Error', 'Ошибка'))); }
    setBusyId(null);
  };

  const openReview = (o) => { setReviewOrder(o); setReviewRating(5); setReviewComment(''); };

  const openShip = (o) => { setShipOrder(o); setShipTracking(o.tracking_number || ''); setShipCarrier(o.carrier || ''); };

  const openTracking = async (carrier, nr) => {
    const hasPortal = !!CARRIER_PORTALS[carrier];
    if (hasPortal) {
      try {
        await navigator.clipboard.writeText(nr);
        toast.success(tr('Numurs nokopēts — ielīmē pārvadātāja lapā', 'Number copied — paste it on the carrier page', 'Номер скопирован — вставьте на странице перевозчика'));
      } catch (_) { /* clipboard may be blocked — link still opens */ }
    }
    window.open(trackingHref(carrier, nr), '_blank', 'noopener');
  };

  const submitShip = async () => {
    if (!shipOrder) return;
    setShipping(true);
    try {
      await axios.post(`${API}/classified-orders/${shipOrder.id}/mark-shipped`, {
        tracking_number: shipTracking.trim() || null,
        carrier: shipCarrier.trim() || null,
      });
      toast.success(tr('Atzīmēts kā nosūtīts/nodots. Pircējs informēts.', 'Marked as shipped/handed over. Buyer notified.', 'Отмечено как отправлено. Покупатель уведомлён.'));
      setShipOrder(null);
      await load();
    } catch (e) { toast.error(formatApiError(e, tr('Kļūda', 'Error', 'Ошибка'))); }
    setShipping(false);
  };

  const submitReview = async () => {
    if (!reviewOrder) return;
    setReviewing(true);
    try {
      await axios.post(`${API}/classified-orders/${reviewOrder.id}/review`, { rating: reviewRating, comment: reviewComment.trim() });
      toast.success(tr('Paldies par atsauksmi!', 'Thanks for the review!', 'Спасибо за отзыв!'));
      setReviewOrder(null);
      await load();
    } catch (e) { toast.error(formatApiError(e, tr('Kļūda', 'Error', 'Ошибка'))); }
    setReviewing(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="classified-deals-page">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <button onClick={() => navigate('/sludinajumi')} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-5" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Uz sludinājumiem', 'Browse listings', 'К объявлениям')}
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#E6F2F0] flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-[#2F7E79]" /></div>
          <h1 className="font-outfit text-3xl font-bold text-[#121212]">{tr('Mani darījumi', 'My deals', 'Мои сделки')}</h1>
        </div>

        {polling && (
          <div className="mb-4 rounded-xl bg-[#E6F2F0] text-[#2F7E79] px-4 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="deals-polling">
            <Loader2 className="w-4 h-4 animate-spin" /> {tr('Pārbaudām apmaksu...', 'Confirming payment...', 'Проверяем оплату...')}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab('buying')} className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold transition-colors ${tab === 'buying' ? 'bg-[#2F7E79] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C]'}`} data-testid="deals-tab-buying">
            <ShoppingBag className="w-4 h-4" /> {tr('Pirkumi', 'Buying', 'Покупки')}
          </button>
          <button onClick={() => setTab('selling')} className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold transition-colors ${tab === 'selling' ? 'bg-[#2F7E79] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C]'}`} data-testid="deals-tab-selling">
            <Store className="w-4 h-4" /> {tr('Pārdošana', 'Selling', 'Продажи')}
          </button>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2F7E79]" /></div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center bg-white border border-[#E5E4E0] rounded-2xl" data-testid="deals-empty">
            <div className="text-5xl mb-3">🤝</div>
            <p className="text-[#5C5C5C]">{tab === 'buying' ? tr('Tev vēl nav pirkumu.', 'No purchases yet.', 'Нет покупок.') : tr('Tev vēl nav pārdošanas darījumu.', 'No sales yet.', 'Нет продаж.')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.paid;
              const img = o.items?.[0]?.image;
              const counterparty = o.is_buyer ? o.seller_name : o.buyer_name;
              return (
                <div key={o.id} className="bg-white border border-[#E5E4E0] rounded-2xl p-4" data-testid={`deal-${o.id}`}>
                  <div className="flex gap-3">
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3F2EE] flex-shrink-0">
                      {img ? <img src={resolveAssetUrl(img)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#C7C5BF]"><ImageOff className="w-6 h-6" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-outfit font-semibold text-[#121212] truncate">{o.items?.map((i) => i.title).join(', ')}</h3>
                        <span className={`flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`} data-testid={`deal-status-${o.id}`}>{tr(meta.lv, meta.en, meta.ru)}</span>
                      </div>
                      <div className="text-[#2F7E79] font-bold text-sm mt-0.5">€{(o.amount || 0).toFixed(2)}</div>
                      <p className="text-xs text-[#8A8A8A] mt-1">
                        {o.is_buyer ? tr('Pārdevējs', 'Seller', 'Продавец') : tr('Pircējs', 'Buyer', 'Покупатель')}: {counterparty || '—'}
                        {o.is_seller && o.status === 'completed' && <> · {tr('tu saņēmi', 'you received', 'вы получили')} €{(o.seller_net || 0).toFixed(2)}</>}
                      </p>
                    </div>
                  </div>

                  {o.status === 'paid' && (
                    <div className="mt-3 pt-3 border-t border-[#E5E4E0] space-y-2.5">
                      {/* Shipment status line (visible to both parties) */}
                      {o.shipped ? (
                        <div className="space-y-1">
                          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-full px-2.5 py-1" data-testid={`deal-shipped-${o.id}`}>
                            <Truck className="w-3.5 h-3.5" /> {tr('Nosūtīts / nodots', 'Shipped / handed over', 'Отправлено / передано')}
                            {o.shipped_at && <span className="font-normal text-green-600/80">· {new Date(o.shipped_at).toLocaleDateString('lv-LV')}</span>}
                          </div>
                          {o.tracking_number && (
                            <div className="text-xs text-[#5C5C5C]" data-testid={`deal-tracking-${o.id}`}>
                              <span className="font-semibold text-[#121212]">{tr('Izsekošanas nr.', 'Tracking no.', 'Трек-номер')}:</span>{' '}
                              <button type="button" onClick={() => openTracking(o.carrier, o.tracking_number)}
                                className="font-mono text-[#2F7E79] hover:text-[#276b66] underline underline-offset-2 inline-flex items-center gap-1" data-testid={`deal-track-link-${o.id}`}>
                                {o.tracking_number} <ExternalLink className="w-3 h-3" />
                              </button>
                              {o.carrier ? <span className="text-[#8A8A8A]"> · {o.carrier}</span> : null}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#8A8A8A]" data-testid={`deal-awaiting-ship-${o.id}`}>
                          <Clock className="w-3.5 h-3.5" /> {o.is_seller
                            ? tr('Atzīmē, kad prece nosūtīta/nodota', 'Mark when shipped/handed over', 'Отметьте при отправке')
                            : tr('Gaida, kad pārdevējs nosūtīs/nodos preci', 'Waiting for the seller to ship/hand over', 'Ожидание отправки продавцом')}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2">
                        {o.is_buyer ? (
                          <>
                            <Button onClick={() => act(o.id, 'complete', tr('Apstiprini, ka saņēmi preci? Nauda tiks pārskaitīta pārdevējam.', 'Confirm you received the item? Funds will be released to the seller.', 'Подтвердить получение?'))} disabled={busyId === o.id}
                              className="bg-green-600 hover:bg-green-700 text-white gap-2 rounded-full" data-testid={`deal-complete-${o.id}`}>
                              {busyId === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {tr('Apstiprinu saņemšanu', 'Confirm receipt', 'Подтвердить')}
                            </Button>
                            <Button variant="outline" onClick={() => act(o.id, 'cancel', tr('Atcelt darījumu un saņemt naudas atmaksu?', 'Cancel the deal and get a refund?', 'Отменить и вернуть деньги?'))} disabled={busyId === o.id}
                              className="border-red-200 text-red-600 hover:bg-red-50 gap-2 rounded-full" data-testid={`deal-cancel-${o.id}`}>
                              <XCircle className="w-4 h-4" /> {tr('Atcelt', 'Cancel', 'Отменить')}
                            </Button>
                          </>
                        ) : (
                          <>
                            {!o.shipped ? (
                              <Button onClick={() => openShip(o)} disabled={busyId === o.id}
                                className="bg-[#2F7E79] hover:bg-[#276b66] text-white gap-2 rounded-full" data-testid={`deal-ship-${o.id}`}>
                                <Truck className="w-4 h-4" /> {tr('Atzīmēt kā nosūtītu / nodotu', 'Mark shipped / handed over', 'Отметить отправленным')}
                              </Button>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1.5 text-xs text-[#8A8A8A]"><Clock className="w-3.5 h-3.5" /> {tr('Gaida, kad pircējs apstiprinās saņemšanu', 'Waiting for the buyer to confirm receipt', 'Ожидание подтверждения')}</span>
                                <Button variant="outline" onClick={() => openShip(o)} className="border-[#2F7E79] text-[#2F7E79] hover:bg-[#E6F2F0] gap-1.5 rounded-full h-8 px-3 text-xs" data-testid={`deal-edit-tracking-${o.id}`}>
                                  <Truck className="w-3.5 h-3.5" /> {o.tracking_number ? tr('Mainīt izsekošanas nr.', 'Edit tracking no.', 'Изменить трек-номер') : tr('Pievienot izsekošanas nr.', 'Add tracking no.', 'Добавить трек-номер')}
                                </Button>
                              </>
                            )}
                            <Button variant="outline" onClick={() => act(o.id, 'cancel', tr('Atcelt darījumu un atmaksāt pircējam?', 'Cancel and refund the buyer?', 'Отменить и вернуть покупателю?'))} disabled={busyId === o.id}
                              className="border-red-200 text-red-600 hover:bg-red-50 gap-2 rounded-full" data-testid={`deal-cancel-${o.id}`}>
                              <XCircle className="w-4 h-4" /> {tr('Atcelt un atmaksāt', 'Cancel & refund', 'Отменить')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {o.status === 'completed' && (
                    <div className="mt-3 pt-3 border-t border-[#E5E4E0] flex flex-wrap items-center gap-3">
                      {o.reviewed_by_me ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700" data-testid={`deal-reviewed-${o.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> {tr('Tava atsauksme pievienota', 'Your review added', 'Отзыв добавлен')}</span>
                      ) : (
                        <>
                          <Button onClick={() => openReview(o)} className="bg-[#D97736] hover:bg-[#c3652b] text-white gap-2 rounded-full" data-testid={`deal-review-${o.id}`}>
                            <Star className="w-4 h-4" /> {tr('Novērtēt darījumu', 'Rate the deal', 'Оценить сделку')}
                          </Button>
                          <span className="text-xs text-[#8A8A8A]">{o.is_buyer ? tr('Novērtē pārdevēju', 'Rate the seller', 'Оцените продавца') : tr('Novērtē pircēju', 'Rate the buyer', 'Оцените покупателя')}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Mutual review dialog */}
      <Dialog open={!!reviewOrder} onOpenChange={(v) => { if (!v) setReviewOrder(null); }}>
        <DialogContent data-testid="deal-review-dialog">
          <DialogHeader><DialogTitle>{tr('Novērtē darījumu', 'Rate the deal', 'Оценить сделку')}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{tr('Atstāj zvaigžņu vērtējumu un komentāru', 'Leave a star rating and a comment', 'Оставьте оценку и комментарий')}</DialogDescription>
          {reviewOrder && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C5C5C]">
                {reviewOrder.is_buyer ? tr('Pārdevējs', 'Seller', 'Продавец') : tr('Pircējs', 'Buyer', 'Покупатель')}: <span className="font-semibold text-[#121212]">{reviewOrder.is_buyer ? reviewOrder.seller_name : reviewOrder.buyer_name}</span>
              </p>
              <div className="flex items-center gap-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setReviewRating(n)} data-testid={`review-star-${n}`} className="p-1">
                    <Star className={`w-8 h-8 transition-colors ${n <= reviewRating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                  </button>
                ))}
              </div>
              <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} rows={3}
                placeholder={tr('Kā noritēja darījums? (nav obligāti)', 'How did the deal go? (optional)', 'Как прошла сделка? (необязательно)')} data-testid="review-comment-input" />
              <Button onClick={submitReview} disabled={reviewing} className="w-full h-11 rounded-full bg-[#2F7E79] hover:bg-[#276b66] text-white font-semibold gap-2" data-testid="submit-review-btn">
                {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} {tr('Iesniegt atsauksmi', 'Submit review', 'Отправить')}
              </Button>
              <p className="text-[11px] text-[#8A8A8A] text-center">{tr('Atsauksme parādīsies publiskajā profilā.', 'The review will appear on the public profile.', 'Отзыв появится в публичном профиле.')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark shipped / tracking dialog */}
      <Dialog open={!!shipOrder} onOpenChange={(v) => { if (!v) setShipOrder(null); }}>
        <DialogContent data-testid="deal-ship-dialog">
          <DialogHeader><DialogTitle>{tr('Nosūtīts / nodots', 'Shipped / handed over', 'Отправлено / передано')}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{tr('Pievieno izsekošanas numuru un pārvadātāju (nav obligāti)', 'Add a tracking number and carrier (optional)', 'Добавьте трек-номер и перевозчика (необязательно)')}</DialogDescription>
          {shipOrder && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C5C5C]">{tr('Prece', 'Item', 'Товар')}: <span className="font-semibold text-[#121212]">{shipOrder.items?.map((i) => i.title).join(', ')}</span></p>
              <div>
                <label className="text-xs font-semibold text-[#121212]">{tr('Pārvadātājs', 'Carrier', 'Перевозчик')}</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {CARRIERS.map((c) => (
                    <button key={c} type="button" onClick={() => setShipCarrier(c === 'Cits' ? '' : c)} data-testid={`ship-carrier-${c}`}
                      className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${shipCarrier === c || (c === 'Cits' && !CARRIERS.slice(0, 4).includes(shipCarrier)) ? 'bg-[#2F7E79] text-white border-[#2F7E79]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#2F7E79]'}`}>
                      {c}
                    </button>
                  ))}
                </div>
                {!CARRIERS.slice(0, 4).includes(shipCarrier) && (
                  <Input value={shipCarrier} onChange={(e) => setShipCarrier(e.target.value)} className="mt-2"
                    placeholder={tr('Cits pārvadātājs (nav obligāti)', 'Other carrier (optional)', 'Другой перевозчик')} data-testid="ship-carrier-input" />
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-[#121212]">{tr('Izsekošanas numurs', 'Tracking number', 'Трек-номер')}</label>
                <Input value={shipTracking} onChange={(e) => setShipTracking(e.target.value)} className="mt-1.5"
                  placeholder={tr('piem., CC123456789LV (nav obligāti)', 'e.g., CC123456789LV (optional)', 'напр., CC123456789LV')} data-testid="ship-tracking-input" />
              </div>
              <Button onClick={submitShip} disabled={shipping} className="w-full h-11 rounded-full bg-[#2F7E79] hover:bg-[#276b66] text-white font-semibold gap-2" data-testid="submit-ship-btn">
                {shipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} {tr('Atzīmēt kā nosūtītu / nodotu', 'Mark shipped / handed over', 'Отметить отправленным')}
              </Button>
              <p className="text-[11px] text-[#8A8A8A] text-center">{tr('Vari atzīmēt arī bez numura (piem., nodots personīgi). Pircējs saņems paziņojumu.', 'You can mark it without a number (e.g., handed over in person). The buyer will be notified.', 'Можно отметить без номера. Покупатель получит уведомление.')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
